import React, { useContext, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Button,
    Chip,
    Stack,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { AnalyticProfileContext, AnalyticProfileContextType } from '../contexts/AnalyticProfileContext';
import { NotebooksContext } from '../contexts/NotebooksContext';
import { api } from '../api/apiService';
import {
    defaultArticlePayload,
    serializePortalDraft,
} from '../portal/portalDraftEnvelope';
import { setNotebookDraftKind, rememberNotebookToOpen } from '../utils/draftNotebookKind';
import { markNotebookDraftLifetimeSave } from '../utils/notebookDraftLifetimeSave';

interface ContentIdea {
    headline: string;
    description: string;
    angles: string[];
}

interface IdeaSparkerCluster {
    cluster_id: string;
    title: string;
    country: string;
    general_overview: string;
    relevance_score: number;
    content_ideas: ContentIdea;
}

interface IdeaSparkerResponse {
    clusters: IdeaSparkerCluster[];
}

function paragraphHtml(text: string): string {
    const escaped = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/\n/g, '<br>');
    return `<p>${escaped}</p>`;
}

type LayoutIdeaSparkerProps = {
    onArticleDraftReady?: () => void;
};

const LayoutIdeaSparker = ({ onArticleDraftReady }: LayoutIdeaSparkerProps) => {
    const { selectedProfileId } = useContext<AnalyticProfileContextType>(AnalyticProfileContext);
    const { addNotebook, fetchNotebooks } = useContext(NotebooksContext);
    const [ideas, setIdeas] = useState<IdeaSparkerCluster[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [hasGenerated, setHasGenerated] = useState<boolean>(false);
    const [generatedAt, setGeneratedAt] = useState<number | null>(null);
    const [startingId, setStartingId] = useState<string | null>(null);

    useEffect(() => {
        const loadSavedIdeas = async () => {
            if (!selectedProfileId) {
                setIdeas([]);
                setGeneratedAt(null);
                setHasGenerated(false);
                return;
            }

            try {
                const response = await api.get(
                    `/api/idea-sparker/profiles/${selectedProfileId}/saved`
                );

                const data = response as {
                    clusters: IdeaSparkerCluster[];
                    generated_at: number | null;
                };

                if (data.clusters && data.clusters.length > 0) {
                    setIdeas(data.clusters);
                    setGeneratedAt(data.generated_at);
                    setHasGenerated(true);
                } else {
                    setIdeas([]);
                    setGeneratedAt(null);
                    setHasGenerated(false);
                }
            } catch (err) {
                console.error('Error loading saved ideas:', err);
                setIdeas([]);
                setGeneratedAt(null);
                setHasGenerated(false);
            }
        };

        loadSavedIdeas();
    }, [selectedProfileId]);

    const generateIdeas = async () => {
        if (!selectedProfileId) {
            setError('Select an analytic profile first.');
            return;
        }

        setIdeas([]);
        setHasGenerated(false);
        setLoading(true);
        setError(null);

        try {
            const initiateResponse = await api.post(
                `/api/idea-sparker/profiles/${selectedProfileId}/generate`,
                {}
            );

            const jobData = initiateResponse as { job_id: string; status: string };
            const jobId = jobData.job_id;
            const pollInterval = 2000;
            const maxAttempts = 90;
            let attempts = 0;

            const checkJobStatus = async (): Promise<void> => {
                if (attempts >= maxAttempts) {
                    throw new Error('Content idea generation timed out. Please try again.');
                }

                attempts++;

                try {
                    const statusResponse = await api.get(`/api/idea-sparker/jobs/${jobId}`);
                    const statusData = statusResponse as {
                        job_id: string;
                        status: string;
                        error?: string;
                        result?: IdeaSparkerResponse;
                    };

                    if (statusData.status === 'completed') {
                        if (statusData.result) {
                            setIdeas(statusData.result.clusters || []);
                            setGeneratedAt(Math.floor(Date.now() / 1000));
                            setHasGenerated(true);
                        } else {
                            throw new Error('No content ideas generated');
                        }
                    } else if (statusData.status === 'failed') {
                        throw new Error(statusData.error || 'Failed to generate content ideas');
                    } else {
                        await new Promise((resolve) => setTimeout(resolve, pollInterval));
                        await checkJobStatus();
                    }
                } catch (err: any) {
                    if (err.response?.status === 404) {
                        throw new Error('Job not found. Please try again.');
                    }
                    throw err;
                }
            };

            await checkJobStatus();
        } catch (err: any) {
            console.error('Error generating content ideas:', err);
            setError(err.message || err.response?.data?.message || 'Failed to generate content ideas. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const startArticleDraft = async (cluster: IdeaSparkerCluster) => {
        const headline = (cluster.content_ideas?.headline || '').trim();
        const description = (cluster.content_ideas?.description || '').trim();
        if (!headline) {
            setError('This idea has no headline to use as the article title.');
            return;
        }

        setStartingId(cluster.cluster_id);
        setError(null);
        try {
            const created = await addNotebook({
                name: headline,
                description: '',
                clusters: [],
                status: 'active',
                visibility: 'private',
            });
            if (!created?.id) {
                throw new Error('Could not create the article draft.');
            }

            const payload = defaultArticlePayload(created.id);
            payload.title = headline;
            payload.excerpt = '';
            payload.content = description;
            payload.content_blocks = description
                ? [{ type: 'paragraph', text: description, html: paragraphHtml(description) }]
                : [];
            const serialized = serializePortalDraft({
                schemaVersion: 1,
                kind: 'article',
                payload,
            });

            await api.put(`/api/notebooks/${created.id}`, {
                title: headline,
                draft: {
                    title: headline,
                    content: serialized,
                    updated_at: Date.now(),
                },
            });
            setNotebookDraftKind(created.id, 'article');
            markNotebookDraftLifetimeSave(created.id);
            await fetchNotebooks();
            rememberNotebookToOpen(created.id);
            onArticleDraftReady?.();
        } catch (err: any) {
            console.error('Error starting article draft:', err);
            setError(err.message || err.response?.data?.message || 'Could not open this idea as an article draft.');
        } finally {
            setStartingId(null);
        }
    };

    if (!selectedProfileId) {
        return (
            <Box sx={{ p: 3, bgcolor: '#0a0a0a', color: '#fafafa', flex: 1 }}>
                <Alert severity="info" sx={{ bgcolor: '#18181b', color: '#e4e4e7' }}>
                    Select an analytic profile in the header to see story ideas.
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ bgcolor: '#0a0a0a', minHeight: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column', color: '#fafafa' }}>
            <Box
                sx={{
                    px: { xs: 2, md: 3 },
                    py: 2,
                    borderBottom: '1px solid #27272a',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                }}
            >
                <Box sx={{ maxWidth: 640 }}>
                    <Typography sx={{ color: '#fafafa', fontWeight: 600, fontSize: '1rem' }}>
                        Story ideas
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#a1a1aa', mt: 0.5 }}>
                        Suggestions from the clusters this profile is tracking. Start a draft when one is worth writing.
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    onClick={generateIdeas}
                    disabled={loading || startingId !== null}
                    startIcon={loading ? <CircularProgress size={16} sx={{ color: '#a1a1aa' }} /> : <AutoAwesomeIcon />}
                    sx={{
                        color: '#fafafa',
                        borderColor: '#3f3f46',
                        textTransform: 'none',
                        '&:hover': { borderColor: '#71717a', bgcolor: '#18181b' },
                        '&.Mui-disabled': { color: '#71717a', borderColor: '#27272a' },
                    }}
                >
                    {loading ? 'Generating…' : hasGenerated ? 'Regenerate' : 'Generate ideas'}
                </Button>
            </Box>

            <Box sx={{ flex: 1, px: { xs: 2, md: 3 }, py: 3 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2, bgcolor: '#18181b', color: '#fecaca' }}>
                        {error}
                    </Alert>
                )}

                {loading && (
                    <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
                        Reading the current clusters and writing suggestions…
                    </Typography>
                )}

                {!loading && ideas.length > 0 && (
                    <Stack spacing={1.5}>
                        {generatedAt ? (
                            <Typography variant="caption" sx={{ color: '#71717a' }}>
                                Generated {new Date(generatedAt * 1000).toLocaleString()}
                            </Typography>
                        ) : null}
                        {ideas.map((cluster) => {
                            const idea = cluster.content_ideas;
                            const angles = idea?.angles || [];
                            const busy = startingId === cluster.cluster_id;
                            return (
                                <Box
                                    key={cluster.cluster_id}
                                    sx={{
                                        p: 2.5,
                                        borderRadius: 2,
                                        bgcolor: '#18181b',
                                        border: '1px solid #27272a',
                                    }}
                                >
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                                        {cluster.country ? (
                                            <Chip
                                                size="small"
                                                label={cluster.country}
                                                sx={{ bgcolor: '#27272a', color: '#d4d4d8', height: 22 }}
                                            />
                                        ) : null}
                                        {cluster.title ? (
                                            <Typography variant="caption" sx={{ color: '#71717a' }}>
                                                {cluster.title}
                                            </Typography>
                                        ) : null}
                                    </Stack>
                                    <Typography sx={{ color: '#fafafa', fontWeight: 600, fontSize: '1.05rem', lineHeight: 1.35 }}>
                                        {idea?.headline || 'Untitled idea'}
                                    </Typography>
                                    {idea?.description ? (
                                        <Typography variant="body2" sx={{ color: '#d4d4d8', mt: 1, lineHeight: 1.6, maxWidth: 720 }}>
                                            {idea.description}
                                        </Typography>
                                    ) : null}
                                    {cluster.general_overview ? (
                                        <Typography variant="body2" sx={{ color: '#71717a', mt: 1.5, lineHeight: 1.55, maxWidth: 720 }}>
                                            {cluster.general_overview}
                                        </Typography>
                                    ) : null}
                                    {angles.length > 0 && (
                                        <Box sx={{ mt: 1.5 }}>
                                            <Typography variant="caption" sx={{ color: '#71717a', letterSpacing: '0.04em' }}>
                                                Angles
                                            </Typography>
                                            <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                                                {angles.map((angle) => (
                                                    <Typography key={angle} variant="body2" sx={{ color: '#a1a1aa' }}>
                                                        {angle}
                                                    </Typography>
                                                ))}
                                            </Stack>
                                        </Box>
                                    )}
                                    <Box sx={{ mt: 2 }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            disabled={startingId !== null}
                                            onClick={() => void startArticleDraft(cluster)}
                                            sx={{
                                                color: '#fafafa',
                                                borderColor: '#3f3f46',
                                                textTransform: 'none',
                                                '&:hover': { borderColor: '#71717a', bgcolor: '#27272a' },
                                                '&.Mui-disabled': { color: '#71717a', borderColor: '#27272a' },
                                            }}
                                        >
                                            {busy ? 'Opening draft…' : 'Start article draft'}
                                        </Button>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                )}

                {!loading && !error && ideas.length === 0 && (
                    <Box
                        sx={{
                            p: 3,
                            borderRadius: 2,
                            bgcolor: '#18181b',
                            border: '1px solid #27272a',
                            maxWidth: 560,
                        }}
                    >
                        <Typography sx={{ color: '#fafafa', fontWeight: 600 }}>
                            {hasGenerated ? 'No ideas came back' : 'No ideas yet'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#a1a1aa', mt: 0.75 }}>
                            {hasGenerated
                                ? 'Generate again once this profile has clusters worth writing about.'
                                : 'Generate suggestions from the clusters this profile is already tracking.'}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default LayoutIdeaSparker;
